/* Best-Preis cart enhancements.
 *
 * Runs against Shopify's native cart. Prices are read live from the recommendations
 * endpoint and never persisted, and the add goes through /cart/add.js so the real
 * cart stays the single source of truth.
 *
 * Three jobs:
 *   1. "Passt dazu" upsell - a scroll rail on phones, a list on desktop.
 *   2. Condition badges on cart lines, with the ERP's title suffix removed.
 *   3. Handing the updated sections back to Horizon so it morphs the cart in place
 *      (its own row-add animation included) instead of reloading the page.
 */
(function () {
  const money = (cents) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  const PACKAGE_RE = /(set|bundle|paket|kalender)/i;

  // Mirrors snippets/bp-condition.liquid: the grade is the trailing title segment.
  const GRADES = {
    defekt: 'Defekt',
    'wie neu': 'Wie neu',
    'sehr gut': 'Sehr gut',
    gut: 'Gut',
    neu: 'Neu',
  };
  const GRADE_KEY = {
    defekt: 'defekt',
    'wie neu': 'wieneu',
    'sehr gut': 'sehrgut',
    gut: 'gut',
    neu: 'neu',
  };
  const SUFFIX_RE = /\s*[–-]\s*(defekt|wie neu|sehr gut|gut|neu)(\s*,\s*unvollständig)?\s*$/i;

  function conditionOf(title) {
    const m = SUFFIX_RE.exec(title || '');
    return m ? m[1].toLowerCase() : '';
  }
  function isIncomplete(title) {
    return /unvollständig/i.test(title || '');
  }
  function cleanTitle(t) {
    return (t || '').replace(SUFFIX_RE, '').trim();
  }

  /* ---------------------------------------------------------------- upsell */

  function eligible(p, inCart) {
    if (!p.available) return false;
    if (inCart.has(String(p.id))) return false;
    if (!p.variants || p.variants.length !== 1) return false;
    if (PACKAGE_RE.test(p.title || '')) return false;
    // A defective article is never offered for one-click add. The negative
    // Beschaffenheitsvereinbarung (Sec. 434 BGB) has to be acknowledged on the
    // product page, and this rail has no gate - so it must not sell one at all.
    if (conditionOf(p.title) === 'defekt') return false;
    // Same guard as every other grid: a 0,00 EUR article is a data error, not an
    // offer. The price is read from whichever shape the payload carries, and an
    // absent price is not treated as zero - a missing field should not silently
    // empty the whole rail.
    const cents =
      p.price != null ? Number(p.price) : p.variants[0] ? Number(p.variants[0].price) : null;
    if (cents != null && !(cents > 0)) return false;
    return true;
  }

  // How far the cart still is from free shipping, in cents. Both numbers are
  // rendered server-side by bp-free-shipping.liquid, so nothing is guessed here.
  function freeShippingGap() {
    const el = document.querySelector('[data-bp-ship]');
    if (!el) return 0;
    const threshold = Number(el.dataset.threshold || 0);
    const subtotal = Number(el.dataset.subtotal || 0);
    const gap = threshold - subtotal;
    return gap > 0 ? gap : 0;
  }

  // Better grades rank higher; when free shipping is within reach, an item that
  // closes the gap without overshooting it wildly is worth more than a cheap one.
  function score(p, gap) {
    const c = conditionOf(p.title);
    let s = c === 'neu' ? 4 : c === 'wie neu' ? 3 : c === 'sehr gut' ? 2 : 1;
    if (gap > 0) {
      const price = p.variants[0].price;
      if (price >= gap && price <= gap * 1.6) s += 4;
      else if (price >= gap * 0.6 && price < gap) s += 2;
    }
    return s + Math.random() * 0.5;
  }

  async function fillRail(rail) {
    const pid = rail.dataset.productId;
    if (!pid) return;
    const key = pid + '|' + rail.dataset.inCart;
    if (rail.dataset.bpKey === key) return;
    rail.dataset.bpKey = key;

    const inCart = new Set((rail.dataset.inCart || '').split(',').filter(Boolean));
    let items = [];
    try {
      const r = await fetch(`/recommendations/products.json?product_id=${pid}&limit=10&intent=complementary`);
      if (r.ok) items = (await r.json()).products || [];
      if (items.length < 3) {
        const r2 = await fetch(`/recommendations/products.json?product_id=${pid}&limit=10&intent=related`);
        if (r2.ok) items = items.concat((await r2.json()).products || []);
      }
    } catch (e) {
      console.error('[bp-upsell] recommendations failed', pid, e);
      rail.hidden = true;
      return;
    }

    const gap = freeShippingGap();
    const seen = new Set();
    const picks = items
      .filter((p) => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return eligible(p, inCart);
      })
      .map((p) => ({ p, s: score(p, gap) }))
      .sort((a, b) => b.s - a.s)
      .slice(0, 3);

    const list = rail.querySelector('[data-bp-upsell-list]');
    if (!picks.length) {
      rail.hidden = true;
      return;
    }

    const note = rail.querySelector('[data-bp-upsell-note]');
    if (note) {
      note.textContent = gap > 0 ? `Noch ${money(gap)} bis zum kostenlosen Versand` : '';
      note.hidden = gap <= 0;
    }

    list.textContent = '';
    picks.forEach(({ p }, i) => {
      const v = p.variants[0];
      const cond = conditionOf(p.title);
      const el = document.createElement('div');
      el.className = 'bp-upsell__item';
      el.style.setProperty('--bp-i', String(i));

      const img = document.createElement('img');
      img.className = 'bp-upsell__img';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 72;
      img.height = 72;
      if (p.featured_image) {
        img.src = p.featured_image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x160$1$2');
      } else {
        img.classList.add('bp-upsell__img--none');
      }

      const body = document.createElement('div');
      body.className = 'bp-upsell__body';

      const a = document.createElement('a');
      a.className = 'bp-upsell__name';
      a.href = p.url;
      a.textContent = cleanTitle(p.title);

      const meta = document.createElement('div');
      meta.className = 'bp-upsell__meta';
      const price = document.createElement('span');
      price.className = 'bp-upsell__price';
      price.textContent = money(v.price);
      meta.appendChild(price);
      if (cond) {
        const b = document.createElement('span');
        b.className = 'bp-badge bp-badge--' + GRADE_KEY[cond];
        b.textContent = GRADES[cond];
        meta.appendChild(b);
      }

      body.appendChild(a);
      body.appendChild(meta);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bp-upsell__btn';
      btn.dataset.variant = String(v.id);
      btn.textContent = '+ Hinzufügen';
      if (cond === 'defekt') {
        // Defective goods need the acknowledgement on the product page first.
        btn.disabled = true;
        btn.title = 'Defekte Artikel bitte über die Produktseite bestätigen';
      }

      el.appendChild(img);
      el.appendChild(body);
      el.appendChild(btn);
      list.appendChild(el);
    });

    rail.hidden = false;
  }

  /* ------------------------------------------------- add, the Horizon way */

  function cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => {
      const id = c.getAttribute('data-section-id');
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  // Horizon's cart listens for `cart:lines-update` and morphs its own section from
  // `event.promise`. Handing it the sections we already fetched avoids a reload and
  // reuses its row-add animation. If none are attached, it re-renders the section itself.
  function notifyCart(sections) {
    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });
    const ev = new Event('cart:lines-update', { bubbles: true, cancelable: false });
    ev.promise = promise;
    document.dispatchEvent(ev);
    resolve({ detail: { sections: sections || {} } });
    return promise;
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.bp-upsell__btn');
    if (!btn || btn.disabled) return;
    const original = btn.textContent;
    btn.disabled = true;
    btn.classList.add('is-busy');
    btn.textContent = '…';
    try {
      const sections = cartSectionIds();
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(btn.dataset.variant), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();

      btn.classList.remove('is-busy');
      btn.classList.add('is-added');
      btn.textContent = 'Hinzugefügt';

      await notifyCart(data.sections);
    } catch (err) {
      console.error('[bp-upsell] add failed', err);
      btn.classList.remove('is-busy');
      btn.disabled = false;
      btn.textContent = original;
    }
  });

  /* ------------------------------------------------ cart line decoration */

  // The ERP appends the grade to every title. In the cart that is redundant next to a
  // badge, so the suffix is moved out of the text and shown as one. Display only -
  // the line item, the order and the invoice keep the ERP's full title.
  function decorateLines(root) {
    const titles = (root || document).querySelectorAll('.cart-items__title:not([data-bp-cond])');
    titles.forEach((el) => {
      el.dataset.bpCond = '1';
      const raw = (el.textContent || '').trim();
      const cond = conditionOf(raw);
      const incomplete = isIncomplete(raw);
      if (!cond && !incomplete) return;

      if (cond) el.textContent = cleanTitle(raw);

      const row = el.closest('.cart-items__title-row') || el.parentElement;
      if (!row || row.querySelector('.bp-badge')) return;

      if (cond) {
        const b = document.createElement('span');
        b.className = 'bp-badge bp-badge--' + GRADE_KEY[cond];
        b.textContent = GRADES[cond];
        row.appendChild(b);
      }
      if (incomplete) {
        const t = document.createElement('span');
        t.className = 'bp-badge bp-badge--teil';
        t.textContent = 'Unvollständig';
        row.appendChild(t);
      }
    });
  }

  /* ------------------------------------------------------------- lifecycle */

  function scan() {
    document.querySelectorAll('[data-bp-upsell]').forEach(fillRail);
    decorateLines(document);
  }

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  }

  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', scan);

  // The cart morphs on every quantity change; re-decorate whatever comes back.
  new MutationObserver(schedule).observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
